import type { Feature, Polygon, MultiPolygon, Position } from "geojson";
import { cutPolygon } from "../../../domain/geometry/operations/cut";
import type { GeometryResult } from "../../../domain/geometry/types";
import type { UndoableCommand } from "../undoStack";

export interface CutPolygonInput {
	polygon: Feature<Polygon>;
	cuttingLine: Position[];
	bufferMeters?: number;
}

export interface CutPolygonResult {
	command: UndoableCommand;
	/** The result of the cut — Polygon if only 1 piece, MultiPolygon if multiple */
	result: Feature<Polygon | MultiPolygon>;
}

/**
 * Cut a polygon with a line. Returns the resulting geometry
 * and an UndoableCommand that applies/reverts via the supplied callbacks.
 */
export function cutPolygonUseCase(
	input: CutPolygonInput,
	applyResult: (result: Feature<Polygon | MultiPolygon>) => void,
	revert: (original: Feature<Polygon>) => void,
): GeometryResult<CutPolygonResult> {
	const cutResult = cutPolygon(input.polygon, input.cuttingLine, input.bufferMeters);
	if (!cutResult.ok) return cutResult;

	const resultGeom = cutResult.data;
	const original = input.polygon;

	return {
		ok: true,
		data: {
			result: resultGeom,
			command: {
				label: "Cut polygon",
				execute: () => applyResult(resultGeom),
				undo: () => revert(original),
			},
		},
	};
}
